/**
 * Parse Gita Seva app dynamic links into website paths.
 *
 * Supported shapes:
 * - /app?link=https%3A%2F%2Fgitaseva.org%2Fapp%3F4%26{id}%26hindi
 * - /app/?link=...
 * - /app?4&{id}&hindi  (unwrapped inner query)
 *
 * Inner query: {type}&{id}&{language?}
 */

export type AppDeepLinkResult = {
  path: string;
  /** hindi | english when present in the link */
  lang?: "hindi" | "english";
  type: string;
  id: string;
};

/** Product type codes used in app share / Firebase dynamic links */
const TYPE_TO_PATH: Record<string, (id: string) => string> = {
  "1": (id) => `/books/${encodeURIComponent(id)}`,
  "2": (id) => `/magazines/kalyan/${encodeURIComponent(id)}`,
  "3": (id) => `/magazines/kalyanakalpataru/${encodeURIComponent(id)}`,
  "4": (id) => `/magazines/geetgovind/${encodeURIComponent(id)}`,
  "5": (id) => `/audios/${encodeURIComponent(id)}`,
  "6": (id) => `/pravachans/${encodeURIComponent(id)}`,
  "7": (id) => `/articles/${encodeURIComponent(id)}`,
  "8": (id) => `/divinequotes/${encodeURIComponent(id)}`,
  "9": (id) => `/magazines/vivekvani/${encodeURIComponent(id)}`,
  "10": (id) => `/DailySatsang/${encodeURIComponent(id)}`,
  "11": (id) => `/videos/${encodeURIComponent(id)}`,
};

const normalizeLang = (value?: string): "hindi" | "english" | undefined => {
  if (!value) return undefined;
  const v = value.trim().toLowerCase();
  if (v === "hindi" || v === "hi") return "hindi";
  if (v === "english" || v === "en") return "english";
  return undefined;
};

/**
 * Extract the inner `{type}&{id}&{lang?}` string from a location search.
 * Uses the raw query so encoded `link=` and raw `?type&id` both work.
 */
export const extractAppDeepLinkQuery = (search: string): string | null => {
  const raw = (search || "").replace(/^\?/, "");
  if (!raw) return null;

  // Encoded Firebase / app share: ?link=https://.../app?type&id&lang
  const linkMatch = raw.match(/(?:^|&)link=([^&]*)/);
  if (linkMatch) {
    let decoded = linkMatch[1];
    try {
      decoded = decodeURIComponent(linkMatch[1]);
    } catch {
      // keep raw encoded value
    }
    // Prefer query after last ? (handles nested app?link=.../app?type&id)
    const qIndex = decoded.lastIndexOf("?");
    if (qIndex >= 0) {
      return decoded.slice(qIndex + 1);
    }
    // link value is already the inner query
    if (/^\d+&/.test(decoded)) {
      return decoded;
    }
    return null;
  }

  // Unwrapped: ?4&mongoId&hindi
  if (/^\d+&/.test(raw)) {
    return raw;
  }

  return null;
};

/**
 * Map type + id (+ optional lang) to a website path.
 */
export const resolveAppDeepLink = (
  search: string
): AppDeepLinkResult | null => {
  const inner = extractAppDeepLinkQuery(search);
  if (!inner) return null;

  const parts = inner.split("&").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;

  const type = parts[0];
  const id = parts[1];
  if (!type || !id) return null;

  const pathBuilder = TYPE_TO_PATH[type];
  if (!pathBuilder) return null;

  const lang = normalizeLang(parts[2]);
  return {
    path: pathBuilder(id),
    lang,
    type,
    id,
  };
};
